import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { CheckCircle2, AlertCircle, Loader2, Lock } from "lucide-react";

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [state, setState] = useState({ status: "loading", message: "" });
  const { refresh } = useAuth();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!token) {
        setState({ status: "error", message: "Missing verification token." });
        return;
      }
      try {
        await api.post("/auth/verify-email", { token });
        if (cancelled) return;
        setState({ status: "ok", message: "Your email has been verified. You're all set." });
        try { await refresh(); } catch (e) { /* ignore */ }
      } catch (err) {
        if (cancelled) return;
        setState({
          status: "error",
          message: formatApiErrorDetail(err.response?.data?.detail) || err.message,
        });
      }
    })();
    return () => { cancelled = true; };
  }, [token, refresh]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4 py-12 bg-grid">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center gap-2 mb-8">
          <div className="w-9 h-9 rounded-lg bg-slate-900 text-white flex items-center justify-center">
            <Lock className="w-4 h-4" />
          </div>
          <span className="font-display font-bold text-xl tracking-tight">ResumeLock</span>
        </Link>
        <Card className="p-8 shadow-sm text-center" data-testid="verify-email-card">
          {state.status === "loading" && (
            <>
              <Loader2 className="w-8 h-8 animate-spin text-slate-400 mx-auto" />
              <p className="mt-4 text-sm text-slate-500">Verifying your email…</p>
            </>
          )}
          {state.status === "ok" && (
            <>
              <div className="w-12 h-12 mx-auto rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center">
                <CheckCircle2 className="w-6 h-6" />
              </div>
              <h1 className="mt-4 font-display text-2xl font-bold tracking-tight">Email verified</h1>
              <p className="text-sm text-slate-500 mt-2">{state.message}</p>
              <Link to="/dashboard">
                <Button className="mt-6 bg-slate-900 hover:bg-slate-800" data-testid="verify-go-dashboard">
                  Continue to dashboard
                </Button>
              </Link>
            </>
          )}
          {state.status === "error" && (
            <>
              <div className="w-12 h-12 mx-auto rounded-xl bg-red-50 text-red-600 flex items-center justify-center">
                <AlertCircle className="w-6 h-6" />
              </div>
              <h1 className="mt-4 font-display text-2xl font-bold tracking-tight">Verification failed</h1>
              <p className="text-sm text-slate-500 mt-2">{state.message}</p>
              <Link to="/dashboard">
                <Button variant="outline" className="mt-6" data-testid="verify-back-dashboard">
                  Back to dashboard
                </Button>
              </Link>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
